import axios from "axios";
import { isGuestUser } from "../utils/guestSession";

const getAuthHeader = () => {
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const access_token = userData?.access_token;
  return access_token ? { Authorization: `Bearer ${access_token}` } : {};
};

export const getProduct = async (slug) => {
  try {
    const response = await axios.get(
      `https://backend.podvolution.com/api/v1/product-by-blueprint/v1?blueprint_id=${slug}`,
      { headers: getAuthHeader() }
    );
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const getStyleOptions = async () => {
  try {
    const response = await axios.get(
      `https://backend.podvolution.com/api/v1/design-options`,
      { headers: getAuthHeader() }
    );
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const getUsersData = async () => {
  try {
    const response = await axios.get(
      `https://backend.podvolution.com/api/v1/admin/users?skip=0&limit=50`,
      { headers: getAuthHeader() }
    );
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const generateImageGuest = async (data) => {
  try {
    // Force device_fingerprint to be a valid string if it's missing
    if (!data.device_fingerprint || data.device_fingerprint === undefined || data.device_fingerprint === null) {
      data.device_fingerprint = 'emergency-fingerprint';
    }
    
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL || 'https://printspaceai.com/api'}/v1/guest/generate_image`,
      data
    );
    
    return response.data;
  } catch (error) {
    console.error("Error generating guest image");
    throw error;
  }
};

export const generateImage = async (data) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    const response = await axios.post(
      `${apiUrl}/v1/generate_image`,
      data,
      { headers: getAuthHeader() }
    );

    return response.data;
  } catch (error) {
    console.error("Error generating image");
    throw error;
  }
};

// ── Remix image (image + prompt → remixed image) ──
export const remixImage = async (data) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    const response = await axios.post(
      `${apiUrl}/v1/remix_image`,
      data,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error remixing image");
    throw error;
  }
};

export const remixImageGuest = async (data) => {
  try {
    if (!data.device_fingerprint || data.device_fingerprint === undefined || data.device_fingerprint === null) {
      data.device_fingerprint = 'emergency-fingerprint';
    }
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL || 'https://printspaceai.com/api'}/v1/guest/remix_image`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error remixing guest image");
    throw error;
  }
};

// ── Style image (async job: POST returns job_id, poll for result) ──
// Avoids nginx 504 — initial response is instant; polling is fast
const STYLE_POLL_INTERVAL = 2500; // 2.5s
const STYLE_POLL_MAX_MS = 180_000; // 3 min max wait

const pollStyleStatus = async (apiUrl, jobId, authHeaderFn, isGuest) => {
  const start = Date.now();
  while (Date.now() - start < STYLE_POLL_MAX_MS) {
    const url = isGuest
      ? `${apiUrl}/v1/guest/style_image/status/${jobId}`
      : `${apiUrl}/v1/style_image/status/${jobId}`;
    const res = await axios.get(url, authHeaderFn ? { headers: authHeaderFn() } : {});
    const { status, result, error } = res.data;
    if (status === 'completed') return result;
    if (status === 'failed') throw new Error(error || 'Style transform failed');
    await new Promise((r) => setTimeout(r, STYLE_POLL_INTERVAL));
  }
  throw new Error('Style transform timed out');
};

export const styleImage = async (data) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const { data: job } = await axios.post(`${apiUrl}/v1/style_image`, data, {
    headers: getAuthHeader(),
    timeout: 15000, // POST returns immediately
  });
  return pollStyleStatus(apiUrl, job.job_id, getAuthHeader, false);
};

export const styleImageGuest = async (data) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  if (!data.device_fingerprint) data.device_fingerprint = 'emergency-fingerprint';
  const { data: job } = await axios.post(`${apiUrl}/v1/guest/style_image`, data, {
    timeout: 15000, // POST returns immediately
  });
  return pollStyleStatus(apiUrl, job.job_id, null, true);
};

export const removeBackgroundImage = async (data) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const response = await axios.post(`${apiUrl}/v1/remove_background`, data, {
    headers: getAuthHeader(),
    timeout: 120000,
  });
  return response.data;
};

export const removeBackgroundImageGuest = async (data) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  if (!data.device_fingerprint) data.device_fingerprint = 'emergency-fingerprint';
  const response = await axios.post(`${apiUrl}/v1/guest/remove_background`, data, {
    timeout: 120000,
  });
  return response.data;
};

// ── Mockup from preview image (sync) ──
export const mockupFromPreview = async (data) => {
  const apiUrl = process.env.REACT_APP_API_URL || "https://printspaceai.com/api";
  const response = await axios.post(
    `${apiUrl}/v1/mockup_from_preview_sync`,
    data,
    { headers: getAuthHeader(), timeout: 120000 }
  );
  return response.data;
};

export const mockupFromPreviewGuest = async (data) => {
  const apiUrl = process.env.REACT_APP_API_URL || "https://printspaceai.com/api";
  if (!data.device_fingerprint) data.device_fingerprint = "emergency-fingerprint";
  const response = await axios.post(
    `${apiUrl}/v1/guest/mockup_from_preview_sync`,
    data,
    { timeout: 120000 }
  );
  return response.data;
};

export const placeOrder = async (data) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("🛒 Placing order with payload:", data);
    console.log("🛒 Using API URL:", apiUrl);
    
    const response = await axios.post(
      `${apiUrl}/v1/place_order`,
      data,
      { headers: getAuthHeader() }
    );

    console.log("✅ Order placed successfully:", response.data);
    return response; // Return the response data
  } catch (error) {
    console.error("❌ Error placing order:", error);
    console.error("❌ Error response:", error.response?.data);
    console.error("❌ Error status:", error.response?.status);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const addgeneratedImage = async (data) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    
    const response = await axios.post(
      `${apiUrl}/v1/upload-design`,
      data,
      { headers: getAuthHeader() }
    );

    return response.data;
  } catch (error) {
    console.error("Error saving image");
    throw error;
  }
};

export const addgeneratedImageUrl = async (data) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    
    const response = await axios.post(
      `${apiUrl}/v1/upload-design-url`,
      data,
      { headers: getAuthHeader() }
    );

    return response.data;
  } catch (error) {
    console.error("Error saving image URL");
    throw error;
  }
};

export const getMyTokens = async (userID) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    
    const response = await axios.get(
      `${apiUrl}/v1/token/balance/${userID}`,
      { headers: getAuthHeader() }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching tokens");
    throw error;
  }
};

export const getMyDesigns = async (userID) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("Get designs API URL:", `${apiUrl}/v1/user-designs?user_id=${userID}`);
    
    const response = await axios.get(
      `${apiUrl}/v1/user-designs?user_id=${userID}`,
      { headers: getAuthHeader() }
    );

    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching designs:", error);
    console.error("API URL used:", process.env.REACT_APP_API_URL);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const addToCart = async (payload) => {
  try {
    const response = await axios.post(
      `https://backend.podvolution.com/api/v1/cart/add`,
      payload,
      { headers: getAuthHeader() }
    );

    return response.data; // Return the response data
  } catch (error) {
    console.error("Error adding to cart", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const viewCart = async (userID) => {
  try {
    const response = await axios.get(
      `https://backend.podvolution.com/api/v1/cart/view?user_id=${userID}`,
      { headers: getAuthHeader() }
    );

    return response.data; // Return the response data
  } catch (error) {
    console.error("Error viewing cart:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const viewOrders = async (userID) => {
  try {
    const response = await axios.get(
      `https://printspaceai.com/api/v1/cart/view/order?user_id=${userID}`,
      { headers: getAuthHeader() }
    );

    return response.data; // Return the response data
  } catch (error) {
    console.error("Error viewing orders:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const getOrderTrackingUrl = async (orderID) => {
  try {
    const response = await axios.get(
      `https://backend.podvolution.com/api/v1/printify/order-status/${orderID}`,
      { headers: getAuthHeader() }
    );

    return response.data; // Return the response data
  } catch (error) {
    console.error("Error viewing orders:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const cancelOrder = async (order_id, cart_id) => {
  try {
    const response = await axios.post(
      `https://backend.podvolution.com/api/v1/printify/cancel-order/${order_id}/${cart_id}`,
      {},
      { headers: getAuthHeader() }
    );

    return response.data; // Return the response data
  } catch (error) {
    return null;
  }
};

export const deleteFromCart = async (transaction_id) => {
  try {
    const response = await axios.delete(
      "https://backend.podvolution.com/api/v1/cart/remove",
      {
        headers: {
          ...getAuthHeader(),
          accept: "application/json",
          "Content-Type": "application/json",
        },
        data: { transaction_id }, // Data must be inside the config object
      }
    );

    return response.data; // Return the response data
  } catch (error) {
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const deleteDesign = async (user_id, image_id) => {
  try {
    const response = await axios.delete(
      "https://backend.podvolution.com/api/v1/delete-design",
      {
        headers: {
          ...getAuthHeader(),
          accept: "application/json",
        },
        params: {
          user_id,
          image_id,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const toggleUserActiveStatus = async (user_id, is_active) => {
  const res = await fetch(
    "https://backend.podvolution.com/api/v1/user/activate-deactivate",
    {
      method: "PUT",
      headers: {
        ...getAuthHeader(),
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({ user_id, is_active }),
    }
  );

  if (!res.ok) throw new Error("Failed to toggle user status");
  return res.json();
};

export const validatePromoCode = async (code) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    const response = await axios.post(
      `${apiUrl}/v1/promo-code/validate`,
      { code: code.toUpperCase() }
    );
    return response.data;
  } catch (error) {
    console.error("Error validating promo code:", error);
    return {
      valid: false,
      message: error.response?.data?.detail || "Invalid promo code"
    };
  }
};

export const createPaymentintent = async (payload) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    const isGuest = isGuestUser();
    
    // Use guest endpoint for guest users, regular endpoint for authenticated users
    const endpoint = isGuest ? '/v1/guest/create-payment-intent' : '/v1/create-payment-intent';
    const headers = isGuest ? {} : getAuthHeader(); // No auth header needed for guest endpoint
    
    console.log("🚀 Creating payment intent with payload:", payload);
    console.log("🚀 Using API URL:", apiUrl);
    console.log("🚀 Using endpoint:", endpoint);
    console.log("🚀 Is guest user:", isGuest);
    console.log("🔍 Address ID being sent:", payload.address_id);
    console.log("🔍 User ID being sent:", payload.user_id);
    
    const response = await axios.post(
      `${apiUrl}${endpoint}`,
      payload,
      { headers }
    );

    console.log("✅ Payment intent created successfully:", response.data);
    return response.data; // Return the response data
  } catch (error) {
    console.error("❌ Payment intent creation failed:", error);
    console.error("❌ Error response:", error.response?.data);
    console.error("❌ Error status:", error.response?.status);
    console.error("❌ Error headers:", error.response?.headers);
    
    // Return error details instead of null
    return {
      error: true,
      message: error.response?.data?.detail || error.message || "Payment intent creation failed",
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/** Upload design to S3 via preview-only endpoint (works for guests — no login required). */
export const uploadPreviewImage = async (imageData) => {
  const apiUrl = process.env.REACT_APP_API_URL || "https://printspaceai.com/api";
  let payload = imageData;
  if (payload && !payload.startsWith("data:") && !payload.startsWith("http")) {
    payload = `data:image/png;base64,${payload}`;
  }
  const response = await axios.post(
    `${apiUrl}/v1/preview_upload_image`,
    { image_data: payload },
    { timeout: 90000 }
  );
  const url = response.data?.image_url;
  if (response.data?.ok && url?.startsWith("http")) {
    return url;
  }
  throw new Error("Preview image upload failed");
};

export const previewDesign = async (payload) => {
  try {
    const variantId = Number(payload?.variant_id);
    const productId = Number(payload?.product_id);
    const providerId = Number(payload?.provider_id);
    const previewDetails = Array.isArray(payload?.preview_details)
      ? payload.preview_details
      : [];

    if (
      !Number.isFinite(variantId) ||
      variantId <= 0 ||
      !Number.isFinite(productId) ||
      productId <= 0 ||
      !Number.isFinite(providerId) ||
      providerId <= 0
    ) {
      console.warn("Skipping preview request due to invalid identifiers", {
        variant_id: payload?.variant_id,
        product_id: payload?.product_id,
        provider_id: payload?.provider_id,
      });
      return null;
    }

    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const hasAuth = userData?.access_token;
    const apiUrl = process.env.REACT_APP_API_URL || "https://printspaceai.com/api";

    const safePayload = {
      ...payload,
      variant_id: variantId,
      product_id: productId,
      provider_id: providerId,
      preview_details: previewDetails,
    };

    const response = await axios.post(
      `${apiUrl}/v1/preview_product`,
      safePayload,
      {
        ...(hasAuth ? { headers: getAuthHeader() } : {}),
        timeout: 180000,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Preview design error:", error);
    console.error("Error response:", error.response?.data);
    return null;
  }
};

export const publishProductToPrintify = async (payload) => {
  const apiUrl = process.env.REACT_APP_API_URL || "https://printspaceai.com/api";
  const candidates = [
    `${apiUrl}/v1/printify/publish-product`,
    `${apiUrl}/v1/admin/printify/publish-product`,
    `https://backend.podvolution.com/api/v1/printify/publish-product`,
  ];

  let lastError = null;

  for (const endpoint of candidates) {
    try {
      const response = await axios.post(endpoint, payload, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        continue;
      }
      throw error;
    }
  }

  throw lastError || new Error("Publish to Printify endpoint is unavailable.");
};

export const saveDraftToPrintify = async (payload) => {
  const apiUrl = process.env.REACT_APP_API_URL || "https://printspaceai.com/api";
  const candidates = [
    `${apiUrl}/v1/printify/save-draft-product`,
    `${apiUrl}/v1/admin/printify/save-draft-product`,
    `https://backend.podvolution.com/api/v1/printify/save-draft-product`,
  ];

  let lastError = null;
  for (const endpoint of candidates) {
    try {
      const response = await axios.post(endpoint, payload, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        continue;
      }
      throw error;
    }
  }

  throw lastError || new Error("Save draft endpoint is unavailable.");
};

export const generatePrintifyMetadata = async (payload) => {
  const apiUrl = process.env.REACT_APP_API_URL || "https://printspaceai.com/api";
  const candidates = [
    `${apiUrl}/v1/printify/generate-metadata`,
    `${apiUrl}/v1/admin/printify/generate-metadata`,
    `https://backend.podvolution.com/api/v1/printify/generate-metadata`,
  ];

  let lastError = null;
  for (const endpoint of candidates) {
    try {
      const response = await axios.post(endpoint, payload, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        continue;
      }
      throw error;
    }
  }

  throw lastError || new Error("Generate metadata endpoint is unavailable.");
};

export const getAddresses = async (userID) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("🏠 Fetching addresses for user:", userID);
    console.log("🏠 Using API URL:", apiUrl);
    
    const response = await axios.get(
      `${apiUrl}/v1/addresses?user_id=${userID}`,
      { headers: getAuthHeader() }
    );

    console.log("✅ Addresses fetched successfully:", response.data);
    return response.data; // Return the response data
  } catch (error) {
    console.error("❌ Error fetching addresses:", error);
    console.error("❌ Error response:", error.response?.data);
    console.error("❌ Error status:", error.response?.status);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const addAddresses = async (payload) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("🏠 Creating address with payload:", payload);
    console.log("🏠 Using API URL:", apiUrl);
    
    const response = await axios.post(
      `${apiUrl}/v1/addresses`,
      payload,
      { headers: getAuthHeader() }
    );

    console.log("✅ Address created successfully:", response.data);
    return response.data; // Return the response data
  } catch (error) {
    console.error("❌ Error adding address:", error);
    console.error("❌ Error response:", error.response?.data);
    console.error("❌ Error status:", error.response?.status);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const updateAddresses = async (addressID, payload) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    const response = await axios.put(
      `${apiUrl}/v1/addresses/${addressID}`,
      payload,
      { headers: getAuthHeader() }
    );

    return response.data; // Return the response data
  } catch (error) {
    console.error("Error updating address:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const deleteAddresses = async (addressID) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    const response = await axios.delete(
      `${apiUrl}/v1/addresses/${addressID}`,
      { headers: getAuthHeader() }
    );

    return response.data; // Return the response data
  } catch (error) {
    console.error("Error deleting address:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const getPriceAddress = async (
  blueprint_id,
  provider_id,
  variant_id
) => {
  if (!blueprint_id || !provider_id || !variant_id) {
    console.warn("getPriceAddress: missing required param", { blueprint_id, provider_id, variant_id });
    return { data: null };
  }
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    const response = await axios.get(
      `${apiUrl}/v1/get-shipping-profile?blueprint_id=${blueprint_id}&provider_id=${provider_id}&variant_id=${variant_id}`,
      { headers: getAuthHeader() }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error;
  }
};

export const getProductMetaData = async (dispatch) => {
  try {
    const response = await axios.get(
      `https://backend.podvolution.com/api/v1/metadata-info`,
      { headers: getAuthHeader() }
    );
    dispatch({
      type: "SET_PRODUCTS_META_DATA",
      data: response?.data?.data,
    });
    // return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const getProducts = async (
  dispatch,
  page = 1,
  page_size = 16,
  category
) => {
  try {
    dispatch({
      type: "SET_PRODUCTS_LOADING",
      loading: true,
    });
    let response = {};
    if (category) {
      response = await axios.get(
        `https://backend.podvolution.com/api/v1/products-by-category?category=${category}&page=${page}&page_size=${page_size}`,
        { headers: getAuthHeader() }
      );
    } else {
      response = await axios.get(
        `https://backend.podvolution.com/api/v1/products?page=${page}&page_size=${page_size}`,
        { headers: getAuthHeader() }
      );
    }
    dispatch({
      type: "SET_PRODUCTS_LOADING",
      loading: false,
    });
    dispatch({
      type: "SET_PRODUCTS",
      data: response?.data,
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const serachProduct = async (
  dispatch,
  page = 1,
  page_size = 16,
  search_text
) => {
  try {
    dispatch({
      type: "SET_PRODUCTS_LOADING",
      loading: true,
    });
    let response = {};

    response = await axios.get(
      `https://backend.podvolution.com/api/v1/search-products?search=${search_text}&page=${page}&page_size=${page_size}`,
      { headers: getAuthHeader() }
    );

    dispatch({
      type: "SET_PRODUCTS_LOADING",
      loading: false,
    });
    dispatch({
      type: "SET_PRODUCTS",
      data: { ...response?.data, search_text },
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

export const serachProductByFilters = async (dispatch, data, search_text) => {
  try {
    dispatch({
      type: "SET_PRODUCTS_LOADING",
      loading: true,
    });
    let response = {};

    response = await axios.post(
      `https://backend.podvolution.com/api/v1/filter-products`,
      data,
      { headers: getAuthHeader() }
    );

    dispatch({
      type: "SET_PRODUCTS_LOADING",
      loading: false,
    });
    dispatch({
      type: "SET_PRODUCTS",
      data: search_text
        ? { ...response?.data, search_text, filter: true }
        : { ...response?.data, filter: true },
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error; // Optionally rethrow the error for handling in the calling function
  }
};

// 1. Download User Prompt Excel
export const downloadUserPromptsExcel = async (userId = null) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    // If userId is provided, filter by user, otherwise get all prompts
    const url = userId 
      ? `${apiUrl}/v1/prompts/export?user_id=${userId}`
      : `${apiUrl}/v1/prompts/export`;
    
    const response = await axios.get(
      url,
      { 
        responseType: "blob",
        headers: getAuthHeader()
      }
    );
    const url_blob = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url_blob;
    link.setAttribute("download", userId ? `user_prompts_${userId}.xlsx` : "all_prompts.xlsx");
    document.body.appendChild(link);
    link.click();
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url_blob);
    }, 100);
  } catch (error) {
    console.error("Error downloading user prompt Excel:", error);
    throw error;
  }
};

// 2. Download Support Info Excel
export const downloadSupportExcel = async () => {
  try {
    const response = await axios.get(
      `https://backend.podvolution.com/api/v1/contact-us/export-excel`,
      { 
        responseType: "blob",
        headers: getAuthHeader()
      }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "support_info.xlsx");
    document.body.appendChild(link);
    link.click();
  } catch (error) {
    console.error("Error downloading support Excel:", error);
  }
};

// 3. Fetch User Summary
export const fetchUserSummary = async (page = 1, pageSize = 10) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("Fetching user summary from:", `${apiUrl}/v1/users/summary`);
    
    const response = await axios.get(
      `${apiUrl}/v1/users/summary?page=${page}&page_size=${pageSize}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching user summary:", error);
    throw error;
  }
};

// 4. Fetch Order Summary
export const fetchOrderSummary = async (page = 1, pageSize = 10) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("Fetching order summary from:", `${apiUrl}/v1/orders-summary`);
    
    const response = await axios.get(
      `${apiUrl}/v1/orders-summary?page=${page}&page_size=${pageSize}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching order summary:", error);
    throw error;
  }
};

// Add this to your product.js
export const updateCartQuantity = async ({ user_id, cart_id, new_quantity }) => {
  return fetch(`https://backend.podvolution.com/api/v1/cart/edit-quantity`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      user_id,
      cart_id,
      new_quantity,
    }),
  }).then((res) => res.json());
};


export const calculateTax = async ({
  zip,
  country,
  state,
  city,
  product_cost_cents,
  shipping_cost_cents,
}) => {
  try {
    const response = await axios.post("https://backend.podvolution.com/api/v1/printify/tax/calculate", {
      zip,
      country,
      state,
      city,
      product_cost_cents,
      shipping_cost_cents,
    });
    return response.data;
  } catch (error) {
    console.error("Tax calculation failed", error);
    throw error;
  }
};

export const getIsTexas = async (payload) => {
  try {
    const response = await axios.post(
      `https://backend.podvolution.com/api/v1/printify/is-texas-state`,
      {
        "zip": payload
      },
      { headers: getAuthHeader() }
    );

    return response.data; // Return the response data
  } catch (error) {
    return null;
  }
};

export const updateUserTokenBalance = async (userId, newBalance) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("Update token balance API URL:", `${apiUrl}/v1/users/token-balance`);
    
    const response = await axios.put(
      `${apiUrl}/v1/users/token-balance`,
      {
        user_id: userId,
        new_balance: newBalance
      },
      { headers: getAuthHeader() }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating token balance:", error);
    throw error;
  }
};

export const getCatalogData = async () => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("Fetching catalog data from:", `${apiUrl}/v1/catalog`);
    
    const response = await axios.get(
      `${apiUrl}/v1/catalog`,
      { 
        headers: getAuthHeader(),
        timeout: 10000, // 10 second timeout
        validateStatus: function (status) {
          return status >= 200 && status < 300; // default
        }
      }
    );
    
    // Validate response structure
    if (!response.data) {
      throw new Error('Empty response received from server');
    }
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'API returned unsuccessful response');
    }
    
    if (!response.data.data || typeof response.data.data !== 'object') {
      throw new Error('Invalid catalog data structure received');
    }
    
    console.log("Catalog data response:", {
      success: response.data.success,
      categoriesCount: Object.keys(response.data.data).length,
      categories: Object.keys(response.data.data)
    });
    
    return response.data;
  } catch (error) {
    console.error("Error fetching catalog data:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url
    });
    
    // Return a consistent error structure
    return {
      success: false,
      message: error.message || 'Failed to fetch catalog data',
      data: {}
    };
  }
};

export const getPrintifyCatalogData = async () => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("Fetching Printify catalog data with variant availability from:", `${apiUrl}/v1/printify-catalog/blueprints`);
    
    // Use the blueprints endpoint that shows all variants with availability status
    const response = await axios.get(
      `${apiUrl}/v1/printify-catalog/blueprints?limit=100`,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      console.log("Printify catalog data with variant availability response:", response.data);
      return response.data;
    } else {
      throw new Error('Failed to fetch Printify catalog data with variant availability');
    }
  } catch (error) {
    console.error("Error fetching Printify catalog data with variant availability:", error);
    // Return empty data instead of throwing to avoid breaking the UI
    return {
      success: false,
      data: [],
      message: "Failed to load Printify catalog with variant availability"
    };
  }
};

export const getAllPrintifyCatalogData = async () => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("Fetching all Printify catalog data from:", `${apiUrl}/v1/printify-catalog/blueprints`);
    
    // Use the blueprints endpoint to get all products with variant availability status
    const response = await axios.get(
      `${apiUrl}/v1/printify-catalog/blueprints?limit=100`,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      console.log("All Printify catalog data response:", response.data);
      return response.data;
    } else {
      throw new Error('Failed to fetch all Printify catalog data');
    }
  } catch (error) {
    console.error("Error fetching all Printify catalog data:", error);
    // Return empty data instead of throwing to avoid breaking the UI
    return {
      success: false,
      data: [],
      message: "Failed to load all Printify catalog data"
    };
  }
};



// ─── Admin Catalog Management ────────────────────────────────────────────────

export const adminFetchPrintifyCatalog = async () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.get(`${apiUrl}/v1/admin/catalog/fetch-printify`, {
    headers: getAuthHeader(),
    timeout: 120000,
  });
  return res.data;
};

export const adminGetCatalogProducts = async (page = 1, limit = 50, search = "", category = "", active = "all", bestseller = "all", brand = "all") => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const params = new URLSearchParams({ page, limit, search, category, active, bestseller, brand });
  const res = await axios.get(`${apiUrl}/v1/admin/catalog/products?${params}`, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const adminUpdateCatalogProduct = async (blueprintId, data) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.put(`${apiUrl}/v1/admin/catalog/products/${blueprintId}`, data, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const adminBulkUpdateProducts = async (blueprintIds, data) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.put(`${apiUrl}/v1/admin/catalog/products/bulk`, {
    blueprint_ids: blueprintIds,
    ...data,
  }, { headers: getAuthHeader() });
  return res.data;
};

export const adminExportCatalog = async () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.post(`${apiUrl}/v1/admin/catalog/export`, {}, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const adminSaveCatalog = async (greenOnly = true) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.post(
    `${apiUrl}/v1/admin/catalog/save?green_only=${greenOnly}`,
    {},
    { headers: getAuthHeader() }
  );
  return res.data;
};

export const adminGetCategories = async () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.get(`${apiUrl}/v1/admin/catalog/categories`, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const adminReclassifyProducts = async () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.get(`${apiUrl}/v1/admin/catalog/reclassify`, {
    headers: getAuthHeader(),
    timeout: 60000,
  });
  return res.data;
};

export const adminStartVariantSync = async (onlyMissing = true) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.post(
    `${apiUrl}/v1/admin/catalog/sync-variants?only_missing=${onlyMissing}`,
    {},
    { headers: getAuthHeader() }
  );
  return res.data;
};

export const adminGetVariantSyncStatus = async () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.get(`${apiUrl}/v1/admin/catalog/sync-variants/status`, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const adminStopVariantSync = async () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.post(`${apiUrl}/v1/admin/catalog/sync-variants/stop`, {}, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getProductDetailFast = async (blueprintId) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
  const res = await axios.get(`${apiUrl}/v1/product-detail/${blueprintId}`, {
    headers: getAuthHeader(),
  });
  return res.data;
};

// ─────────────────────────────────────────────────────────────────────────────

export const getCatalogProduct = async (productId) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://printspaceai.com/api';
    console.log("Fetching catalog product from:", `${apiUrl}/v1/catalog`);
    
    const response = await axios.get(
      `${apiUrl}/v1/catalog`,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      // Search for the product in all catalog categories
      const catalogData = response.data.data;
      let foundProduct = null;
      
      for (const [categoryKey, products] of Object.entries(catalogData)) {
        const product = products.find(p => p.id.toString() === productId.toString());
        if (product) {
          foundProduct = product;
          break;
        }
      }
      
      if (foundProduct) {
        return {
          success: true,
          data: foundProduct
        };
      } else {
        throw new Error('Product not found in catalog');
      }
    } else {
      throw new Error('Failed to load catalog data');
    }
  } catch (error) {
    console.error("Error fetching catalog product:", error);
    throw error;
  }
};
